import { useEffect } from "react";
import { useFloorPlanStore } from "@/stores/floorPlan";
import { useUIStore } from "@/stores/uiStore";
import { cn } from "@/lib/utils";
import type { EditorTool, ViewMode } from "@byk/shared";
import { LayoutPresetsMenu } from "./LayoutPresetsMenu";

// Keyboard shortcut mapping
const TOOL_SHORTCUTS: Record<string, EditorTool> = {
  s: "select",
  v: "select", // V for select (common convention, like Photoshop)
  w: "wall",
  i: "window", // 'i' for wIndow since 'w' is wall
  d: "door",
  m: "measure",
};

// AI sparkle icon
const AiIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

const tools: { id: EditorTool; label: string; shortcut: string; icon: React.ReactNode }[] = [
  {
    id: "select",
    label: "Select",
    shortcut: "S",
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
      </svg>
    ),
  },
  {
    id: "wall",
    label: "Draw Wall",
    shortcut: "W",
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
      </svg>
    ),
  },
  {
    id: "window",
    label: "Window",
    shortcut: "I",
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h16v16H4V4zm8 0v16M4 12h16" />
      </svg>
    ),
  },
  {
    id: "door",
    label: "Door",
    shortcut: "D",
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 3v18M6 21h12M6 3h12l-4 18H6L8 3z" />
      </svg>
    ),
  },
  {
    id: "measure",
    label: "Measure",
    shortcut: "M",
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
      </svg>
    ),
  },
];

const viewModes: { id: ViewMode; label: string }[] = [
  { id: "2d", label: "2D" },
  { id: "split", label: "Split" },
  { id: "3d", label: "3D" },
];

export const Toolbar: React.FC = () => {
  const {
    activeTool,
    viewMode,
    snapToGrid,
    gridSize,
    room,
    selectedId,
    volumes,
    pendingPlacement,
  } = useFloorPlanStore();

  const {
    setTool,
    setViewModeWithTransition,
    toggleSnapToGrid,
    setGridSize,
    setRoom,
    generateRectangularWalls,
    removeVolume,
    clearAll,
    select,
    moveVolume,
    rotateVolume,
    cancelPlacement,
  } = useFloorPlanStore();

  // UI Store for panel management - setActiveRightSection auto-expands sidebar
  const { activeRightSection, setActiveRightSection } = useUIStore();

  const selectedVolume = volumes.find((v) => v.id === selectedId);

  // Keyboard shortcuts for tools, object manipulation, and navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      // ESC to cancel placement, deselect, and reset to Select tool
      if (e.key === "Escape") {
        e.preventDefault();
        if (pendingPlacement) {
          cancelPlacement();
        }
        select(null);
        setTool("select"); // Always reset to select tool on Escape
        return;
      }

      // Tab to cycle through objects
      if (e.key === "Tab") {
        e.preventDefault();
        if (volumes.length === 0) return;

        const currentIndex = selectedId
          ? volumes.findIndex((v) => v.id === selectedId)
          : -1;

        let nextIndex: number;
        if (e.shiftKey) {
          // Shift+Tab: previous
          nextIndex = currentIndex <= 0 ? volumes.length - 1 : currentIndex - 1;
        } else {
          // Tab: next
          nextIndex = currentIndex >= volumes.length - 1 ? 0 : currentIndex + 1;
        }

        select(volumes[nextIndex].id);
        return;
      }

      // Object manipulation (only when something is selected)
      if (selectedVolume) {
        // Arrow keys for movement
        const step = e.shiftKey ? 1 : e.ctrlKey || e.metaKey ? 100 : 10;

        switch (e.key) {
          case "ArrowUp":
            e.preventDefault();
            moveVolume(selectedId!, {
              x: selectedVolume.position.x,
              y: selectedVolume.position.y - step,
              z: selectedVolume.position.z,
            });
            return;
          case "ArrowDown":
            e.preventDefault();
            moveVolume(selectedId!, {
              x: selectedVolume.position.x,
              y: selectedVolume.position.y + step,
              z: selectedVolume.position.z,
            });
            return;
          case "ArrowLeft":
            e.preventDefault();
            moveVolume(selectedId!, {
              x: selectedVolume.position.x - step,
              y: selectedVolume.position.y,
              z: selectedVolume.position.z,
            });
            return;
          case "ArrowRight":
            e.preventDefault();
            moveVolume(selectedId!, {
              x: selectedVolume.position.x + step,
              y: selectedVolume.position.y,
              z: selectedVolume.position.z,
            });
            return;
          case "Delete":
          case "Backspace":
            e.preventDefault();
            removeVolume(selectedId!);
            return;
        }

        // R for rotation (check lowercase)
        if (e.key.toLowerCase() === "r" && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          const rotationStep = e.shiftKey ? -90 : 90;
          const newRotation = (selectedVolume.rotation + rotationStep + 360) % 360;
          rotateVolume(selectedId!, newRotation);
          return;
        }
      }

      // Tool shortcuts (only if no modifier keys for movement)
      if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
        const key = e.key.toLowerCase();
        const tool = TOOL_SHORTCUTS[key];
        if (tool) {
          e.preventDefault();
          setTool(tool);
          return;
        }

        // View mode shortcuts: [ and ] to cycle, or v+number for direct access
        const viewModeOrder: ViewMode[] = ["2d", "split", "3d"];
        const currentViewIndex = viewModeOrder.indexOf(viewMode);

        if (e.key === "[") {
          e.preventDefault();
          const prevIndex = currentViewIndex === 0 ? viewModeOrder.length - 1 : currentViewIndex - 1;
          setViewModeWithTransition(viewModeOrder[prevIndex]);
          return;
        }

        if (e.key === "]") {
          e.preventDefault();
          const nextIndex = currentViewIndex === viewModeOrder.length - 1 ? 0 : currentViewIndex + 1;
          setViewModeWithTransition(viewModeOrder[nextIndex]);
          return;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [setTool, select, selectedId, selectedVolume, volumes, moveVolume, rotateVolume, removeVolume, pendingPlacement, cancelPlacement, viewMode, setViewModeWithTransition]);

  return (
    <div className="flex h-full w-full items-center justify-between">
      {/* Left: Tools */}
      <div className="flex items-center gap-1">
        {tools.map((tool) => (
          <button
            key={tool.id}
            onClick={() => setTool(tool.id)}
            className={cn(
              "flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              activeTool === tool.id
                ? "bg-blue-100 text-blue-700"
                : "text-gray-600 hover:bg-gray-100"
            )}
            title={`${tool.label} (${tool.shortcut})`}
          >
            {tool.icon}
            <span className="hidden sm:inline">{tool.label}</span>
          </button>
        ))}

        <div className="mx-2 h-6 w-px bg-gray-200" />

        {/* Quick actions */}
        <button
          onClick={generateRectangularWalls}
          className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
          title="Generate rectangular room walls"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h16v16H4V4z" />
          </svg>
        </button>

        {selectedVolume && (
          <button
            onClick={() => removeVolume(selectedId!)}
            className="rounded-lg px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
            title="Delete selected"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}

        <div className="mx-2 h-6 w-px bg-gray-200" />

        {/* AI Layout Assistant - uses setActiveRightSection to auto-expand sidebar */}
        <button
          onClick={() => setActiveRightSection("ai")}
          className={cn(
            "flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
            activeRightSection === "ai"
              ? "bg-gradient-to-r from-blue-100 to-purple-100 text-purple-700"
              : "text-gray-600 hover:bg-gray-100"
          )}
          title="AI Layout Assistant"
          data-testid="suggest-layout-toolbar-button"
        >
          <AiIcon />
          <span className="hidden sm:inline">AI Layout</span>
        </button>

        {/* BYK-6: AI Render - uses setActiveRightSection to auto-expand sidebar */}
        <button
          onClick={() => setActiveRightSection("render")}
          className={cn(
            "flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
            activeRightSection === "render"
              ? "bg-gradient-to-r from-purple-100 to-pink-100 text-pink-700"
              : "text-gray-600 hover:bg-gray-100"
          )}
          title="Generate AI Render"
          data-testid="render-toolbar-button"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span className="hidden sm:inline">Render</span>
        </button>

        {/* BYK-13: BOM / Quote Panel - uses setActiveRightSection to auto-expand sidebar */}
        <button
          onClick={() => setActiveRightSection("quote")}
          className={cn(
            "flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
            activeRightSection === "quote"
              ? "bg-green-100 text-green-700"
              : "text-gray-600 hover:bg-gray-100"
          )}
          title="Toggle Quote / BOM Panel"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
          <span className="hidden lg:inline">Quote</span>
        </button>
      </div>

      {/* Center: View Mode Toggle + Layout Presets */}
      <div className="flex items-center gap-2">
        {/* View Mode Toggle - BYK-39 Smooth Transitions */}
        <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-1">
          {viewModes.map((mode) => (
            <button
              key={mode.id}
              onClick={() => setViewModeWithTransition(mode.id)}
              className={cn(
                "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
                viewMode === mode.id
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              )}
              title={`${mode.label} view (press [ or ] to cycle)`}
            >
              {mode.label}
            </button>
          ))}
        </div>

        {/* Layout Presets - BYK-169 */}
        <LayoutPresetsMenu />
      </div>

      {/* Right: Settings */}
      <div className="flex items-center gap-4">
        {/* Room dimensions */}
        <div className="flex items-center gap-2 text-sm">
          <label className="text-gray-500">Room:</label>
          <input
            type="number"
            value={room.width}
            onChange={(e) => setRoom({ width: Number(e.target.value) })}
            className="w-16 rounded border px-2 py-1 text-center text-sm"
            min={100}
            max={1000}
            step={10}
          />
          <span className="text-gray-400">x</span>
          <input
            type="number"
            value={room.depth}
            onChange={(e) => setRoom({ depth: Number(e.target.value) })}
            className="w-16 rounded border px-2 py-1 text-center text-sm"
            min={100}
            max={1000}
            step={10}
          />
          <span className="text-gray-400">cm</span>
        </div>

        <div className="h-6 w-px bg-gray-200" />

        {/* Grid settings */}
        <div className="flex items-center gap-2">
          <button
            onClick={toggleSnapToGrid}
            className={cn(
              "rounded px-2 py-1 text-xs font-medium",
              snapToGrid
                ? "bg-blue-100 text-blue-700"
                : "bg-gray-100 text-gray-600"
            )}
          >
            Snap: {snapToGrid ? "ON" : "OFF"}
          </button>
          <select
            value={gridSize}
            onChange={(e) => setGridSize(Number(e.target.value))}
            className="rounded border px-2 py-1 text-xs"
          >
            <option value={5}>5cm</option>
            <option value={10}>10cm</option>
            <option value={20}>20cm</option>
            <option value={50}>50cm</option>
          </select>
        </div>

        <div className="h-6 w-px bg-gray-200" />

        {/* Clear all */}
        <button
          onClick={() => {
            if (confirm("Clear all walls and products?")) {
              clearAll();
            }
          }}
          className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-red-600"
        >
          Clear
        </button>
      </div>
    </div>
  );
};

export default Toolbar;
